import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { TenantLandingPage } from './pages/TenantLandingPage';
import { HostLandingPage } from './pages/HostLandingPage';
import { RoommateLandingPage } from './pages/RoommateLandingPage';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { WaitingListModal } from './components/modals/WaitingListModal';
import { Notification } from './components/ui/Notification';

export default function App() {
  const getPageFromUrl = () => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    
    if (path === '/landlord' || hash === '#landlord' || hash === '#/landlord') {
      return 'host-landing';
    }
    if (path === '/roommate' || hash === '#roommate' || hash === '#/roommate') {
      return 'roommate-landing';
    }
    return 'tenant-landing'; // default/first page
  };

  const [currentPage, setCurrentPage] = useState(getPageFromUrl());
  const [isWaitingListModalOpen, setIsWaitingListModalOpen] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '' });

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(getPageFromUrl());
    };
    
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handlePopState);
    };
  }, []);

  const handleJoinWaitingList = () => {
    setIsWaitingListModalOpen(true);
  };

  const navigateTo = (page: string) => {
    let path = '/';
    let targetPage = 'tenant-landing';
    
    if (page === 'host-landing' || page === 'landlord') {
      path = '/landlord';
      targetPage = 'host-landing';
    } else if (page === 'roommate-landing' || page === 'roommate') {
      path = '/roommate';
      targetPage = 'roommate-landing';
    } else {
      path = '/';
      targetPage = 'tenant-landing';
    }
    
    // Smoothly update browser URL without page reload
    window.history.pushState(null, '', path);
    setCurrentPage(targetPage);
    window.scrollTo(0, 0);
  };

  const showSuccessNotification = (name: string) => {
    setNotification({
      show: true,
      message: `Thanks ${name}! You've been provided early access.`
    });
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'host-landing':
        return <HostLandingPage navigateTo={navigateTo} onJoinWaitingList={handleJoinWaitingList} />;
      case 'roommate-landing':
        return <RoommateLandingPage navigateTo={navigateTo} onJoinWaitingList={handleJoinWaitingList} />;
      case 'tenant-landing':
      default:
        return <TenantLandingPage navigateTo={navigateTo} onJoinWaitingList={handleJoinWaitingList} />;
    }
  };

  return (
    <div className="bg-white text-black font-sans min-h-screen flex flex-col pt-24">
      <Notification 
          message={notification.message} 
          show={notification.show} 
          onHide={() => setNotification({ show: false, message: '' })} 
      />

      <Header 
        navigateTo={(page) => {
          if (page === 'waiting-list') {
            handleJoinWaitingList();
          } else {
            navigateTo(page);
          }
        }}
        onLoginClick={handleJoinWaitingList} 
        currentPage={currentPage}
      />

      <main className="flex-grow">
        <AnimatePresence mode="wait">
          <div key={currentPage}>
            {renderPage()}
          </div>
        </AnimatePresence>
      </main>

      <Footer />

      <AnimatePresence>
        {isWaitingListModalOpen && (
          <WaitingListModal 
            onClose={() => setIsWaitingListModalOpen(false)} 
            onSuccess={showSuccessNotification}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
